<template>
  <div style="width: 100%; height: 100%">
    <div class="equipmentList">
      <div class="container">
        <div>
          <span>{{ $t('module.title') }}</span>
        </div>
        <div>
          <a-space>
            <a-button type="primary" @click="getList">{{ $t('buttons.refresh')}}</a-button>
          </a-space>
        </div>
      </div>
      <a-table
        row-key="id"
        :columns="columns"
        :scroll="{ x: 1800 }"
        :data-source="list"
        :loading="isLoading"
      >
        <template v-slot:status="text">
          <span v-for="(s, i) in text" :key="s">
            <span :style="{ color: (s && runningStatus[s]) ? runningStatus[s].color : '' }">{{ (s && runningStatus[s]) ? runningStatus[s][lang] : '' }}</span>
            <span v-if="i < text.length - 1">&nbsp;&nbsp;</span>
          </span>
        </template>
        <template v-slot:type="text">
          {{ typeMap[text] }}
        </template>
        <template v-slot:enabled="text, record">
          <a-switch
            v-if="text"
            :checked-children="$t('device.no')"
            :un-checked-children="$t('device.yes')"
            :checked="text"
            @change="showConfirm(record)"
          />
          <span v-else>{{ $t('device.yes') }}</span>
        </template>
        <template v-slot:dtuStatus="text">
          {{ dtuStatusMap[text] }}
        </template>
      </a-table>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { getDict } from '@/api/dict'
import { getUnRemovedDTUList, updateDevice } from '@/api/device'

export default {
  data() {
    return {
      columns: [
        {
          title: () => this.$t('device.code'),
          dataIndex: 'code',
          key: 'code'
        },
        {
          title: () => this.$t('common.deviceName'),
          dataIndex: 'name',
          key: 'name'
        },
        {
          title: () => this.$t('common.model'),
          dataIndex: 'model',
          key: 'model'
        },
        {
          title: () => this.$t('device.status'),
          dataIndex: 'status',
          key: 'status',
          scopedSlots: {
            customRender: 'status'
          }
        },
        {
          title: () => this.$t('common.deviceType'),
          dataIndex: 'type',
          key: 'type',
          scopedSlots: {
            customRender: 'type'
          }
        },
        {
          title: () => this.$t('common.protocolCode'),
          dataIndex: 'protocolCode',
          key: 'protocolCode'
        },
        {
          title: () => this.$t('common.dtuCode'),
          dataIndex: 'dtuCode',
          key: 'dtuCode'
        },
        {
          title: () => this.$t('device.dtuStatus'),
          dataIndex: 'dtuStatus',
          key: 'dtuStatus',
          scopedSlots: {
            customRender: 'dtuStatus'
          }
        },
        {
          title: () => this.$t('common.driverType'),
          dataIndex: 'driverType',
          key: 'driverType'
        },
        {
          title: () => this.$t('device.enable'),
          dataIndex: 'enabled',
          key: 'enabled',
          scopedSlots: {
            customRender: 'enabled'
          }
        },
        {
          title: () => this.$t('module.powerOnTime'),
          dataIndex: 'powerOnTime',
          key: 'powerOnTime'
        },
        {
          title: () => this.$t('common.createdAt'),
          dataIndex: 'createdAt',
          key: 'createdAt',
          width: 160
        },
        {
          title: () => this.$t('common.updatedAt'),
          dataIndex: 'updatedAt',
          key: 'updatedAt',
          width: 160
        }
      ],
      list: [],
      statusMap: {},
      typeMap: {},
      dtuStatusMap: {},
      isLoading: false,
      runningStatus: {
        online: {
          color: "#0eed24",
          zh: "在线",
          en: 'Online'
        },
        running: {
          color: "#0eed24",
          zh: "运行",
          en: 'Running'
        },
        maintain: {
          color: "#ff681c",
          zh: "保养",
          en: 'Maintain'
        },
        error: {
          color: "#e82127",
          zh: "故障",
          en: 'Error'
        },
        standby: {
          color: "#11d7f5",
          zh: "停机",
          en: 'Standby'
        },
        sleep: {
          color: "#11d7f5",
          zh: "待机",
          en: 'Sleep'
        },
        offline: {
          color: "#4d4d4d",
          zh: "离线",
          en: 'Offline'
        },
        prepare: {
          color: "#4d4d4d",
          zh: "备机",
          en: 'Prepare'
        }
      }
    }
  },
  computed: {
    lang() {
      return this.$store.state.lang
    }
  },
  created() {
    this.setStatusMap()
    this.setTypeMap()
    this.setDtuStatusMap()
  },
  mounted() {
    this.getList()
  },
  methods: {
    async setStatusMap() {
      try {
        const { data } = await getDict(this.lang === 'en' ? 'en-US' : 'zh-CN', 'deviceStatus')
        data.content.forEach(c => this.statusMap[c.value] = c.text)
      } catch(e) {
        console.log(e)
      }
    },
    async setTypeMap() {
      try {
        const { data } = await getDict(this.lang === 'en' ? 'en-US' : 'zh-CN', 'deviceType')
        data.content.forEach(c => this.typeMap[c.value] = c.text)
      } catch(e) {
        console.log(e)
      }
    },
    async setDtuStatusMap() {
      try {
        const { data } = await getDict(this.lang === 'en' ? 'en-US' : 'zh-CN', 'dtuStatus')
        data.content.forEach(c => this.dtuStatusMap[c.value] = c.text)
      } catch(e) {
        console.log(e)
      }
    },
    async getList() {
      this.isLoading = true
      try {
        const { data } = await getUnRemovedDTUList()
        this.list = data.map(d => {
          d.status = (d.status || '').split(',')
          d.createdAt = moment(+new Date(d.createdAt)).format('YYYY-MM-DD HH:mm:ss')
          d.updatedAt = moment(+new Date(d.updatedAt)).format('YYYY-MM-DD HH:mm:ss')
          return d
        })
      } catch(e) {
        console.log(e)
      } finally {
        this.isLoading = false
      }
    },
    async showConfirm(record) {
      this.$confirm({
        title: this.$t('messages.ctd'),
        content: this.$t('messages.confirmD'),
        onOk: async () => {
          record.enabled = !record.enabled
          const form = {
            id: record.id,
            code: record.code,
            name: record.name,
            type: record.type,
            status: (record.status || []).join(','),
            enabled: record.enabled,
            deviceAddress: record.deviceAddress.length < 2 ? "0" + record.deviceAddress : record.deviceAddress,
            model: record.model,
            brand: record.brand,
            customerId: 7980,
            installAddress: record.installAddress,
            driverType: record.driverType,
            protocolCode: record.protocolCode,
            measurement: record.measurement,
            dtuCode: 'T' + record.dtuCode,
            remark: record.remark,
            isStationDevice: record.isStationDevice,
            initialValue: record.initialValue,
            power: record.power,
            pressure: record.pressure,
            warrantyPeriod: null,
            intervalTime: record.intervalTime,
            tag: record.tag || null
          }
          try {
            const { message } = await updateDevice(form)
            this.$message.success(message)
            this.getList()
          } catch(e) {
            console.log(e)
          }
        },
        onCancel() {
          record.enabled = record.enabled
        }
      })
    }
  }
}
</script>

<style lang="less" scoped>
.equipmentList {
  padding: 15px;
  width: 100%;
  height: 100%;
}
.equipmentList .container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 20px;
}
</style>